
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {JournalBusinessResponse} from './JournalBusinessResponse';

/**
 * JournalBusinessRequest struct
 */
class JournalBusinessRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!string=} module
   * @param {!Date=} timeFrom
   * @param {!Date=} timeTo
   * @param {!string=} searchText
   * @param {!boolean=} caseSensitive
   * @constructor
   */
  constructor (parent = new Request(), module = '', timeFrom = new Date(0), timeTo = new Date(0), searchText = '', caseSensitive = false) {
    super()
    super.copy(parent)
    this.Module = module
    this.TimeFrom = timeFrom
    this.TimeTo = timeTo
    this.SearchText = searchText
    this.CaseSensitive = caseSensitive
  }

  /**
   * Copy struct (shallow copy)
   * @this {!JournalBusinessRequest}
   * @param {!JournalBusinessRequest} other Other struct
   * @returns {!JournalBusinessRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Module != null) {
      this.Module = other.Module
    } else {
      this.Module = null
    }
    if (other.TimeFrom != null) {
      if (other.TimeFrom instanceof Date) {
        this.TimeFrom = new Date(other.TimeFrom.getTime())
      } else {
        this.TimeFrom = new Date(Math.round(other.TimeFrom / 1000000))
      }
    } else {
      this.TimeFrom = null
    }
    if (other.TimeTo != null) {
      if (other.TimeTo instanceof Date) {
        this.TimeTo = new Date(other.TimeTo.getTime())
      } else {
        this.TimeTo = new Date(Math.round(other.TimeTo / 1000000))
      }
    } else {
      this.TimeTo = null
    }
    if (other.SearchText != null) {
      this.SearchText = other.SearchText
    } else {
      this.SearchText = null
    }
    if (other.CaseSensitive != null) {
      this.CaseSensitive = other.CaseSensitive
    } else {
      this.CaseSensitive = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!JournalBusinessRequest}
   * @returns {!JournalBusinessRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new JournalBusinessRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new JournalBusinessRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!JournalBusinessRequest}
   * @param {!JournalBusinessRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof JournalBusinessRequest)) {
      throw new TypeError('Instance of JournalBusinessRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!JournalBusinessRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Module: ((this.Module != null) ? this.Module : null),
      TimeFrom: ((this.TimeFrom != null) ? (this.TimeFrom.getTime() * 1000000) : null),
      TimeTo: ((this.TimeTo != null) ? (this.TimeTo.getTime() * 1000000) : null),
      SearchText: ((this.SearchText != null) ? this.SearchText : null),
      CaseSensitive: ((this.CaseSensitive != null) ? this.CaseSensitive : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return JournalBusinessRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!JournalBusinessRequest} other Object value
   * @returns {!JournalBusinessRequest} Created struct
   */
  static fromObject (other) {
    return new JournalBusinessRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!JournalBusinessRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return JournalBusinessRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!JournalBusinessRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6460
  }
}

export { JournalBusinessRequest };

/**
 * Fast Binary Encoding JournalBusinessRequest field model
 */
class FieldModelJournalBusinessRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.Module = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.TimeFrom = new fbe.FieldModelTimestamp(buffer, this.Module.fbeOffset + this.Module.fbeSize)
    this.TimeTo = new fbe.FieldModelTimestamp(buffer, this.TimeFrom.fbeOffset + this.TimeFrom.fbeSize)
    this.SearchText = new fbe.FieldModelString(buffer, this.TimeTo.fbeOffset + this.TimeTo.fbeSize)
    this.CaseSensitive = new fbe.FieldModelBool(buffer, this.SearchText.fbeOffset + this.SearchText.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Module.fbeSize + this.TimeFrom.fbeSize + this.TimeTo.fbeSize + this.SearchText.fbeSize + this.CaseSensitive.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Module.fbeExtra + this.TimeFrom.fbeExtra + this.TimeTo.fbeExtra + this.SearchText.fbeExtra + this.CaseSensitive.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelJournalBusinessRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6460
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Module.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Module.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Module.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeFrom.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimeTo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.SearchText.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SearchText.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SearchText.fbeSize

    if ((fbeCurrentSize + this.CaseSensitive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CaseSensitive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CaseSensitive.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!JournalBusinessRequest} fbeValue Default value, defaults is new JournalBusinessRequest()
   * @returns {!JournalBusinessRequest} JournalBusinessRequest value
   */
  get (fbeValue = new JournalBusinessRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!JournalBusinessRequest} fbeValue JournalBusinessRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Module.fbeSize) <= fbeStructSize) {
      fbeValue.Module = this.Module.get()
    } else {
      fbeValue.Module = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Module.fbeSize

    if ((fbeCurrentSize + this.TimeFrom.fbeSize) <= fbeStructSize) {
      fbeValue.TimeFrom = this.TimeFrom.get()
    } else {
      fbeValue.TimeFrom = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeFrom.fbeSize

    if ((fbeCurrentSize + this.TimeTo.fbeSize) <= fbeStructSize) {
      fbeValue.TimeTo = this.TimeTo.get()
    } else {
      fbeValue.TimeTo = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimeTo.fbeSize

    if ((fbeCurrentSize + this.SearchText.fbeSize) <= fbeStructSize) {
      fbeValue.SearchText = this.SearchText.get()
    } else {
      fbeValue.SearchText = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SearchText.fbeSize

    if ((fbeCurrentSize + this.CaseSensitive.fbeSize) <= fbeStructSize) {
      fbeValue.CaseSensitive = this.CaseSensitive.get(false)
    } else {
      fbeValue.CaseSensitive = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CaseSensitive.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelJournalBusinessRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelJournalBusinessRequest}
   * @param {!JournalBusinessRequest} fbeValue JournalBusinessRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelJournalBusinessRequest}
   * @param {JournalBusinessRequest} fbeValue JournalBusinessRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Module.set(fbeValue.Module)
    this.TimeFrom.set(fbeValue.TimeFrom)
    this.TimeTo.set(fbeValue.TimeTo)
    this.SearchText.set(fbeValue.SearchText)
    this.CaseSensitive.set(fbeValue.CaseSensitive)
  }
}

export { FieldModelJournalBusinessRequest };

/**
 * Fast Binary Encoding JournalBusinessRequest model
 */
class JournalBusinessRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelJournalBusinessRequest(this.buffer, 4)
  }

  /**
   * Get the JournalBusinessRequest model
   * @this {!JournalBusinessRequestModel}
   * @returns {!FieldModelJournalBusinessRequest} model JournalBusinessRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!JournalBusinessRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!JournalBusinessRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return JournalBusinessRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!JournalBusinessRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelJournalBusinessRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!JournalBusinessRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!JournalBusinessRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!JournalBusinessRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!JournalBusinessRequestModel}
   * @param {!JournalBusinessRequest} value JournalBusinessRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!JournalBusinessRequestModel}
   * @param {!JournalBusinessRequest} value JournalBusinessRequest value, defaults is new JournalBusinessRequest()
   * @return {!object} Deserialized JournalBusinessRequest value and its size
   */
  deserialize (value = new JournalBusinessRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new JournalBusinessRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new JournalBusinessRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!JournalBusinessRequestModel}
   * @param {!number} prev Previous JournalBusinessRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { JournalBusinessRequestModel };
JournalBusinessRequest.__has_response = true;
JournalBusinessRequest.__response_class = JournalBusinessResponse;
